import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { ActivatedRoute } from '@angular/router';
import moment, { Moment } from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { StartEnd } from 'src/app/core/models/start-end.mode';
import { TuwaiqConfig } from '../../core/constants/tuwaiq.config';
import { FilterDetails } from '../../core/models/filter-details.model';
import { RequestViewService } from '../../core/services/request-view.service';
import { DatePickerCustomHeaderComponent } from '../../shared/components/date-picker-custom-header/date-picker-custom-header.component';
import { StateManagementService } from '../state-managemnet.service';

@Component({
    selector: 'app-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrls: ['./date-filter.component.css']
})
export class DateFilterComponent implements OnInit, OnDestroy {
    startDateFilterKey = TuwaiqConfig.REQUEST_QUERY_PARAMS.CREATION_START_DATE_FILTER;
    endDateFilterKey = TuwaiqConfig.REQUEST_QUERY_PARAMS.CREATION_END_DATE_FILTER;
    dateFilterForm: FormGroup;
    startDateFormControl = 'startDateFormControl';
    endDateFormControl = 'endDateFormControl';
    noDateSelected = true;
    invalidEndDateError = false;
    filterDetailsList = [];
    selectedStartDate;
    selectedEndDate;

    private destroy$ = new Subject<void>();
    
    @ViewChild('datepickerFooter', { static: false })
    datepickerFooter: ElementRef;
    @ViewChild('picker', { static: false })
    dateRangePicker: MatDateRangePicker<any>;
    customHeader = DatePickerCustomHeaderComponent;

    // Add flag to prevent duplicate API calls
    private isSettingFromState = false;

    @Input() maxDate: Date = new Date();
    @Input() isNext: boolean = false;

    @Output() dateChange = new EventEmitter<any>();

    constructor(
        public formBuilder: FormBuilder,
        private requestViewService: RequestViewService,
        private stateManagementService: StateManagementService,
        private route: ActivatedRoute
    ) {
        this.initForm();
    }
    ngOnInit(): void {
        this.setInitForm();
        this.CheckParams();
    }
    CheckParams() {
        // First check URL params
        const params = this.route.snapshot.queryParams;
        if (params['startDate'] && params['endDate']) {
            const startDate = moment(params['startDate'], 'YYYY-MM-DD').startOf('day');
            const endDate = moment(params['endDate'], 'YYYY-MM-DD').startOf('day');

            this.dateFilterForm.patchValue({
                startDateFormControl: startDate.toDate(),
                endDateFormControl: endDate.toDate()
            });

            this.noDateSelected = false;
            this.selectedStartDate = startDate;
            this.selectedEndDate = endDate;
        }
        
        this.stateManagementService.currentState$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(state => {
            if (this.isSettingFromState) {
                return; // Skip if we're already handling state changes
            }
            if (state.startDate && state.endDate) {
                this.isSettingFromState = true;

                const startDate = moment(state.startDate, 'YYYY-MM-DD').startOf('day');
                const endDate = moment(state.endDate, 'YYYY-MM-DD').startOf('day');

                console.log('Setting form values from state:', {
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                });

                this.dateFilterForm.patchValue({
                    startDateFormControl: startDate.toDate(),
                    endDateFormControl: endDate.toDate()
                });

                // Force change detection
                setTimeout(() => {
                    this.dateFilterForm.updateValueAndValidity();
                    this.noDateSelected = false;
                });

                this.selectedStartDate = startDate;
                this.selectedEndDate = endDate;

                // Reset flag after a short delay
                setTimeout(() => {
                    this.isSettingFromState = false;
                }, 500);
            }
        });

        this.dateFilterForm.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(values => {
            console.log('Form values changed:', values);
        });
    }
    private initForm() {
        this.dateFilterForm = this.formBuilder.group({
            startDateFormControl: [''],
            endDateFormControl: ['']
        });
    }
    private setInitForm() {
        const selectedDate = localStorage.getItem('selectedDate') ? JSON.parse(localStorage.getItem('selectedDate')) : null;

        this.dateFilterForm = this.formBuilder.group({
            startDateFormControl: [selectedDate ? selectedDate.start : ''],
            endDateFormControl: [selectedDate ? selectedDate.end : '']
        });
        this.noDateSelected = !(selectedDate?.start && selectedDate?.end);
    }

    onDatepickerOpen() {
        this.appendFooter();
    }

    onDatepickerClose() {
        const startDateValue = this.dateFilterForm.getRawValue().startDateFormControl;
        const endDateValue = this.dateFilterForm.getRawValue().endDateFormControl;

        if (!endDateValue && startDateValue) {
            if (!this.noDateSelected) {
                this.handleOnlyStartDateSelected();
            }
        } else if (startDateValue && endDateValue) {
            this.requestViewService.isActionForRequestList.next(true);
            this.handleOnDatesSelected(startDateValue, endDateValue);
        }
    }

    handleOnDatesSelected(startDateValue: Moment, endDateValue: Moment) {
        this.noDateSelected = false;
        this.invalidEndDateError = false;
        const formattedStartDate = moment(startDateValue).format('YYYY-MM-DD');
        const formattedEndDate = moment(endDateValue).format('YYYY-MM-DD');

        if (this.selectedStartDate !== formattedStartDate || this.selectedEndDate !== formattedEndDate) {
            // Update state
            this.stateManagementService.updateState({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                page: 1
            });

            this.getRequestListOnFilterValueChange(formattedStartDate, formattedEndDate, false);
        }

        // store start and end dates
        this.selectedStartDate = formattedStartDate;
        this.selectedEndDate = formattedEndDate;
    }

    handleOnlyStartDateSelected() {
        this.noDateSelected = false;
        this.invalidEndDateError = true;
    }

    getRequestListOnFilterValueChange(startDateValue: string, endDateValue: string, removeFilter: boolean) {
        this.filterDetailsList = [];
          // Explicitly add page parameter to reset to page 1
        this.storeDateFilterDetails(TuwaiqConfig.REQUEST_QUERY_PARAMS.PAGE, 1, removeFilter);
        this.storeDateFilterDetails(this.startDateFilterKey, startDateValue, removeFilter);
        this.storeDateFilterDetails(this.endDateFilterKey, endDateValue, removeFilter);
        const monthStartAndEndDates: StartEnd = { start: startDateValue, end: endDateValue };
        this.passToParent(monthStartAndEndDates);
        this.requestViewService.filterRequestList.next(this.filterDetailsList);
    }

    storeDateFilterDetails(key: string, value, removeFilter: boolean) {
        const sortByFilterDetails: FilterDetails = {
            key: key,
            value: value,
            removeFilter: removeFilter
        };
        this.filterDetailsList.push(sortByFilterDetails);
    }
    passToParent(selectedDate: any | null) {
        this.dateChange.emit(selectedDate);
    }
    onAllDaysBtnClick() {
        if (!this.noDateSelected) {
            // Clear state
            this.stateManagementService.updateState({
                startDate: null,
                endDate: null
            });

            this.getRequestListOnFilterValueChange('', '', true);
            this.noDateSelected = true;
            this.invalidEndDateError = false;
            this.dateRangePicker.close();
            this.dateFilterForm.patchValue({
                startDateFormControl: '',
                endDateFormControl: ''
            });
        }
        this.requestViewService.isActionForRequestList.next(false);
        localStorage.setItem('selectedDate', JSON.stringify(''));

        this.dateRangePicker.close();
    }

    private appendFooter() {
        const matCalendar = document.getElementsByClassName('mat-datepicker-content')[0] as HTMLElement;
        matCalendar.appendChild(this.datepickerFooter.nativeElement);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}